import React, { useState, useEffect } from "react";
import { useAuth } from "react-oidc-context";
import styles from "../POIList/POIList.module.css";
import {
    DefaultButton,
    PrimaryButton,
    IStackTokens,
    Stack,
    StackItem,
    TextField,
    Dialog,
    DialogType,
    DialogFooter,
} from "@fluentui/react";
import { Dropdown, IDropdownOption } from "@fluentui/react/lib/Dropdown";
import { initializeIcons } from "@fluentui/font-icons-mdl2";
import strings from "../../../loc/strings";
import { UpdateContactById, CreateContact, fetchCPByMail } from "../../../services/ManageSystemServices/ContactPersonService";
import { ContactItem } from "../../../model/ManagePOIListItem";
import { LS_USER_INFO, PERSON_CONTACT_TYPE, TEAM_CONTACT_TYPE } from "../../../constants/EiopaFnPConstants";
import { EIOPAError } from "../../../model/EIOPAError";
import ExistsDialog from "../Dialogs/ExistsContactDialog";
import ConfirmDialog from "../Dialogs/ConfirmContactDialog";

initializeIcons();

const stackTokens: IStackTokens = { childrenGap: 20 };

const isEmail = (email: string) =>
    /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,24}$/i.test(email);

function AddEditDialog(props: any) {
    const auth = useAuth();

    let userInfo = { username: "", ncaCode: "", roles: [] };
    const userInfoLS: string = localStorage.getItem(LS_USER_INFO) as string;
    if (userInfoLS !== null) {
        userInfo = JSON.parse(userInfoLS);
    }

    const initialContact = {
        "id": undefined,
        "contactTypeId": PERSON_CONTACT_TYPE,
        "country": null,
        "authority": userInfo.ncaCode,
        "position": null,
        "teamName": null,
        "firstName": null,
        "lastName": null,
        "email": null,
        "professionalPhoneNumber": null,
        "createdDate": new Date().toISOString(),
        "createdBy": auth.user?.profile.email,
        "contactType": null,
        "legalFrameworkDepartment": null
    };

    const type: IDropdownOption[] = [
        { key: PERSON_CONTACT_TYPE, text: "Person" },
        { key: TEAM_CONTACT_TYPE, text: "Team" },
    ];

    const [addEditItem, setAddEditItem] = React.useState<ContactItem>({ ...initialContact });
    const [error, setError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [mailExists, setMailExists] = useState(false);
    const [email, setEmail] = useState(null);
    const [dialogIsHiddenExists, setDialogIsHiddenExists] = React.useState(true);
    const [dialogIsHiddenConfirm, setDialogIsHiddenConfirm] = React.useState(true);
    const [formSubmitted, setFormSubmitted] = useState(false);

    if (Object.keys({ ...props.item }).length != 0 && addEditItem.id !== props.item.id) {
        setAddEditItem(props.item);
        setEmail(props.item.email)
    }

    const handleSubmit = (event: any) => {
        event.preventDefault();
    }

    const resetField = () => {
        setAddEditItem({ ...initialContact });
        setMailExists(false);
        setDialogIsHiddenExists(true);
        setDialogIsHiddenConfirm(true);
        setEmail(null)
    }

    const handleDialogClose = () => {
        props.setDialogIsHidden(true);
        setError('');
        setEmailError('');
        setFormSubmitted(false);
        resetField();
    };

    function checkMail(mail: string, isEncryptedMail?: boolean) {
        (async function () {
            const _itemsExist = await fetchCPByMail(mail, auth.user?.id_token, isEncryptedMail);
            if (_itemsExist.length > 0) {
                setMailExists(true);
            }
            else {
                setDialogIsHiddenConfirm(false);
            }
        })();
    }

    useEffect(() => {
        if (mailExists && email !== addEditItem.email) {
            setDialogIsHiddenExists(false);
        } else if (mailExists && email === addEditItem.email) {
            setDialogIsHiddenConfirm(false);
        }
    }, [mailExists]);

    useEffect(() => {
        if(formSubmitted) {
            checkFormEmail();
        }
    }, [addEditItem.email]);

    const checkFormEmail= ():boolean  => {
        console.log('checkFormEmail');
        if (addEditItem.email && !isEmail(addEditItem.email)) {
            setEmailError('Wrong email');
            return false;
        } else if (!addEditItem.email) {
            setEmailError('Field is required');
            return false;
        } else {
            setEmailError('');
            return true;
        }
    }
    const handleActionExist = (response: string) => {
        setMailExists(false);
        setDialogIsHiddenExists(true);
        setDialogIsHiddenConfirm(true);
        switch (response) {
            case "existsClose":
                break;
            case "existsSave":
                const encryptedId = addEditItem.encryptedId;
                let item = addEditItem;
                if (encryptedId != undefined) {
                    Object.keys(item).forEach((k) => item[k as keyof ContactItem] == "" && delete item[k as keyof ContactItem]);
                    UpdateContactById(encryptedId, addEditItem, auth.user?.id_token)
                        .then((response) => {
                            // Successful update, handle the response accordingly
                            console.log('Contact item updated successfully:', response);
                            // Update the user interface, refresh the data list, etc.
                            if (response == true) {
                                resetField();
                                props.handleActionResponse('cpedittrue');
                                handleDialogClose();
                            } else {
                                if (response == 'The edited Contact already exists in the system') {
                                    console.error('Error updating Contact item:', response);
                                    props.handleActionResponse('cpeditexist');
                                }
                                else {
                                    console.error('Error updating Contact item:', response);
                                    props.handleActionResponse('cpeditfalse');
                                    resetField();
                                }
                            }
                        })
                        .catch((error) => {
                            console.error('Error updating Contact item:', error);
                            resetField();

                            let serverErrorMessage = 'cpeditfalse'; // default fallback message

                            if (error instanceof EIOPAError) {
                                try {
                                    const parsed = JSON.parse(error.message);
                                    if (Array.isArray(parsed.errors) && parsed.errors.length > 0) {
                                        // Use the specific error message from the API
                                        serverErrorMessage = parsed.errors[0];
                                        props.handleActionResponse('error', serverErrorMessage);
                                        return;
                                    } else {
                                        serverErrorMessage = error.message;
                                    }
                                } catch {
                                    serverErrorMessage = error.message;
                                }
                            }

                            props.handleActionResponse(serverErrorMessage);
                        }).finally(() => {
                            setFormSubmitted(false);
                        });

                    handleDialogClose();
                }
                else {
                    if (item.contactTypeId === PERSON_CONTACT_TYPE) {
                        item.teamName = null;
                    }
                    else {
                        item.firstName = null;
                        item.lastName = null;
                        item.position = null;
                    }
                    item.authority = userInfo.ncaCode;
                    Object.keys(item).forEach((k) => item[k as keyof ContactItem] == null && delete item[k as keyof ContactItem]);
                    Object.keys(item).forEach((k) => item[k as keyof ContactItem] == undefined && delete item[k as keyof ContactItem]);
                    Object.keys(item).forEach((k) => item[k as keyof ContactItem] == "" && delete item[k as keyof ContactItem]);
                    setAddEditItem(item);
                    CreateContact(item, auth.user?.id_token)
                        .then((response) => {
                            // Successful create, handle the response accordingly
                            console.log('Contact item created successfully:', response);
                            // Update the user interface, refresh the data list, etc.
                            if (response == true) {
                                resetField();
                                props.handleActionResponse('cpcreatetrue');
                                handleDialogClose();
                            } else {
                                if (response == 'The Contact you are trying to create, already exists in the system') {
                                    console.error('Error creating Contact item:', response);
                                    props.handleActionResponse('cpcreateexist');
                                }
                                else {
                                    console.error('Error creating Contact item:', response);
                                    props.handleActionResponse('cpcreatefalse');
                                    resetField();
                                }
                            }
                        })
                        .catch((error) => {
                            // If there is an error during update, handle it
                            console.error('Error creating Contact item:', error);

                            let serverErrorMessage = 'cpcreatefalse'; // default fallback message

                            if (error instanceof EIOPAError) {
                                try {
                                    const parsed = JSON.parse(error.message);
                                    if (Array.isArray(parsed.errors) && parsed.errors.length > 0) {
                                        // Use the specific error message from the API
                                        serverErrorMessage = parsed.errors[0];
                                        props.handleActionResponse('error', serverErrorMessage);
                                        return;
                                    } else {
                                        serverErrorMessage = error.message;
                                    }
                                } catch {
                                    serverErrorMessage = error.message;
                                }
                            }

                            props.handleActionResponse(serverErrorMessage);
                            resetField();
                        }).finally(() => {
                        setFormSubmitted(false);
                    });
                    handleDialogClose();
                }
                break;
        }
    }

    const handleDialogSave = (id: number, item: ContactItem) => {

        setFormSubmitted(true);
        let emailIsValid = checkFormEmail();

        let flag = false;
        const isAdd = props.isAdd;

        if (item.contactTypeId === PERSON_CONTACT_TYPE) {
            if (!(item.firstName && item.lastName && emailIsValid)) {
                setError('Field is required');
            } else {
                setError('');
                flag = true;
            }
        } else if (item.contactTypeId === TEAM_CONTACT_TYPE) {
            if (!(item.teamName && emailIsValid)) {
                setError('Field is required');
            } else {
                flag = true;
                setError('');
            }
        }

        // If the case is Add or Update the email, send the actual email. Otherwise, send the encrypted email
        if (flag) {
            switch (isAdd) {
                case true:
                    checkMail(item.email ?? '', false);
                    break;
                case false:
                    if (props.item.email !== item.email) {
                        checkMail(item.email ?? '', false);
                    } else {
                        checkMail(item.encryptedEmail ?? '', true);
                    }
                    break;
                default:
                    break;
            }
        }
    };

    // Dialog Edit Contact
    const dialogContentPropsEdit = {
        type: DialogType.normal,
        title: props.title,
    };

    const modalPropsEdit = React.useMemo(
        () => ({
            isBlocking: true,
        }),
        [],
    );

    let actionDis: boolean = false;
    if (props.item.id != undefined) {
        actionDis = true;
    }

    const onChangeDropdown = (event: any, option: any, index: any) => {
        props.setSelectedContactTypeInDialog(option.key);
        setAddEditItem({ ...initialContact, contactTypeId: option.key });
        setError('');
    };

    let SelectedDialog: any;
    switch (addEditItem.contactTypeId ? addEditItem.contactTypeId : props.selectedContactTypeInDialog) {
        case PERSON_CONTACT_TYPE:
            SelectedDialog = (
                <Person
                    addEditItem={addEditItem}
                    setAddEditItem={setAddEditItem}
                    error={error}
                    emailError={emailError}
                />
            );
            break;
        case TEAM_CONTACT_TYPE:
            SelectedDialog = (
                <Team
                    addEditItem={addEditItem}
                    setAddEditItem={setAddEditItem}
                    error={error}
                    emailError={emailError}
                />
            );
            break;
        default:
            SelectedDialog = <div />;
            break;
    }

    return (
        <div className="Contact">
            <form onSubmit={handleSubmit}>
                <Dialog
                    hidden={props.dialogIsHidden}
                    onDismiss={handleDialogClose}
                    dialogContentProps={dialogContentPropsEdit}
                    modalProps={modalPropsEdit}
                    minWidth="560px"
                >
                    <Stack horizontal tokens={stackTokens}>
                        <Dropdown
                            label="Type of contact"
                            options={type}
                            selectedKey={props.selectedContactTypeInDialog}
                            disabled={actionDis}
                            onChange={onChangeDropdown}
                            className={styles.inputFieldFull}
                        />
                    </Stack>
                    {SelectedDialog}
                    <DialogFooter>
                        <PrimaryButton onClick={() => handleDialogSave(addEditItem.id ? addEditItem.id : 0, addEditItem)} text={props.isAdd ? 'Add' : 'Save'} />
                        <DefaultButton onClick={handleDialogClose} text="Cancel" />
                    </DialogFooter>
                    <ExistsDialog
                        dialogIsHiddenExists={dialogIsHiddenExists}
                        setDialogIsHiddenExists={setDialogIsHiddenExists}
                        title="Contact exists"
                        handleActionExist={handleActionExist}
                    />
                    <ConfirmDialog
                        dialogIsHiddenConfirm={dialogIsHiddenConfirm}
                        setDialogIsHiddenConfirm={setDialogIsHiddenConfirm}
                        title={props.isAdd ? "Create contact" : "Edit contact"}
                        isAdd={!!props.isAdd}
                        handleActionExist={handleActionExist}
                    />
                </Dialog>
            </form>
        </div>

    );
}

function Person(props: any) {

    const onChangeField = (event: any) => {
        props.setAddEditItem({ ...props.addEditItem, [event.target.name]: event.target.value.trim().length > 0 ? event.target.value.trim() : null });
    }

    return (
        <div>
            <Stack horizontal tokens={stackTokens}>
                <StackItem grow={2}>
                    <TextField label={strings.requestForInfoPOIFirstName} defaultValue={props.addEditItem.firstName || ""} name="firstName" placeholder="Type name" className={styles.inputField} onChange={onChangeField} required errorMessage={!props.addEditItem.firstName && props.error ? props.error : ""} />
                </StackItem>
                <StackItem grow={2}>
                    <TextField label={strings.requestForInfoPOILastName} defaultValue={props.addEditItem.lastName || ""} name="lastName" placeholder="Type name" className={styles.inputField} onChange={onChangeField} required errorMessage={!props.addEditItem.lastName && props.error ? props.error : ""} />
                </StackItem>
            </Stack>
            <Stack horizontal tokens={stackTokens}>
                <StackItem grow={2}>
                    <TextField label={strings.positionLabel} defaultValue={props.addEditItem.position || ""} name="position" placeholder="Type position" className={styles.inputFieldFull} onChange={onChangeField} />
                </StackItem>
            </Stack>
            <Stack horizontal tokens={stackTokens}>
                <StackItem grow={2}>
                    <TextField label={strings.requestForInfoEmailLabel} defaultValue={props.addEditItem.email || ""} name="email" placeholder="Type email" className={styles.inputFieldFull} required onChange={onChangeField} errorMessage={props.emailError} />
                </StackItem>
            </Stack>
            <Stack horizontal tokens={stackTokens}>
                <StackItem grow={2}>
                    <TextField type="tel" label={strings.phoneLabel} defaultValue={props.addEditItem.professionalPhoneNumber || ""} name="professionalPhoneNumber" placeholder="Type telephone number" className={styles.inputFieldFull + ' ' + styles.numberField} onChange={onChangeField} />
                </StackItem>
            </Stack>
            <Stack horizontal tokens={stackTokens}>
                <StackItem grow={2}>
                    <TextField type="text" label={strings.legalFrameworkDepartment} defaultValue={props.addEditItem.legalFrameworkDepartment || ""} name="legalFrameworkDepartment" placeholder="Type legal framework - department" className={styles.inputFieldFull} onChange={onChangeField} />
                </StackItem>
            </Stack>
        </div>
    );
}

function Team(props: any) {

    const onChangeField = (event: any) => {
        props.setAddEditItem({ ...props.addEditItem, [event.target.name]: event.target.value.trim().length > 0 ? event.target.value.trim() : null });
    }

    return (
        <div>
            <Stack horizontal tokens={stackTokens}>
                <StackItem grow={2}>
                    <TextField label={strings.teamNameLabel} defaultValue={props.addEditItem.teamName || ""} name="teamName" placeholder="Type name" className={styles.inputField} onChange={onChangeField} required errorMessage={!props.addEditItem.teamName && props.error ? props.error : ""} />
                </StackItem>
            </Stack>
            <Stack horizontal tokens={stackTokens}>
                <StackItem grow={2}>
                    <TextField label={strings.requestForInfoEmailLabel} defaultValue={props.addEditItem.email || ""} name="email" placeholder="Type email" className={styles.inputFieldFull} required onChange={onChangeField} errorMessage={props.emailError} />
                </StackItem>
            </Stack>
            <Stack horizontal tokens={stackTokens}>
                <StackItem grow={2}>
                    <TextField type="tel" label={strings.phoneLabel} defaultValue={props.addEditItem.professionalPhoneNumber || ""} name="professionalPhoneNumber" placeholder="Type telephone number" className={styles.inputFieldFull} onChange={onChangeField} />
                </StackItem>
            </Stack>
            <Stack horizontal tokens={stackTokens}>
                <StackItem grow={2}>
                    <TextField type="text" label={strings.legalFrameworkDepartment} defaultValue={props.addEditItem.legalFrameworkDepartment || ""} name="legalFrameworkDepartment" placeholder="Type legal framework - department" className={styles.inputFieldFull} onChange={onChangeField} />
                </StackItem>
            </Stack>
        </div>
    );
}

export default AddEditDialog;
